import React, { useState } from 'react';
import { EvalModel } from '../../types';

const CONFIRM_WORD = 'update';

interface NetworkConfigDialogProps {
  model: EvalModel;
  onSave: (params: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}

/**
 * Edits a servable model's network config (its params).
 *
 * The params vocabulary belongs to Ray, so nothing is validated here beyond "is it JSON" — the
 * save round-trips through the backend to Ray, and whatever Ray objects to comes back as the
 * error message. Editing takes effect on the next catalog refresh even while the model is serving
 * traffic, so the chains at risk are spelled out and the save is gated behind a typed confirmation.
 *
 * The editor is seeded with the model's full stored config because PATCH replaces `params` wholesale
 * rather than merging: dropping a key from this JSON is a request to reset it, and the backend
 * refuses that unless the value is sent explicitly.
 */
export const NetworkConfigDialog: React.FC<NetworkConfigDialogProps> = ({
  model,
  onSave,
  onCancel,
}) => {
  const [paramsText, setParamsText] = useState(() => JSON.stringify(model.params ?? {}, null, 2));
  const [confirmInput, setConfirmInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chains = model.assigned_chains ?? [];
  const isServing = model.is_default || chains.length > 0;
  const confirmed = confirmInput === CONFIRM_WORD;

  const prettify = () => {
    try {
      setParamsText(JSON.stringify(JSON.parse(paramsText || '{}'), null, 2));
      setError(null);
    } catch {
      setError('Config must be valid JSON to prettify');
    }
  };

  const submit = async () => {
    if (!confirmed || submitting) return;
    setError(null);
    let params: Record<string, unknown>;
    try {
      params = JSON.parse(paramsText || '{}');
    } catch {
      setError('Config must be valid JSON');
      return;
    }
    setSubmitting(true);
    try {
      await onSave(params);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update network config');
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (!submitting && e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 24,
          width: 560,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}
        >
          Edit network config
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {model.model_name}
          </strong>{' '}
          — verified by Ray on save. This replaces the whole config, so keep every key you want
          to keep; unknown keys and omitted values are rejected.
        </div>

        {isServing && (
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.6,
              border: '1px solid var(--warning)',
              background: 'color-mix(in srgb, var(--warning) 10%, transparent)',
              borderRadius: 6,
              padding: '10px 12px',
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>This model is serving traffic</div>
            {model.is_default && (
              <div>
                It is the <strong>default {model.model_type}</strong> model.
              </div>
            )}
            {chains.length > 0 && (
              <div>
                Assigned to {chains.length} chain{chains.length === 1 ? '' : 's'}:{' '}
                <strong>{chains.map((c) => c.chain_name).join(', ')}</strong>.
              </div>
            )}
            <div style={{ marginTop: 4 }}>
              The new config takes effect on the next catalog refresh.
            </div>
          </div>
        )}

        <div className="eval-form-group">
          <label className="eval-form-label">Config (JSON)</label>
          <textarea
            className="eval-form-textarea"
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            rows={14}
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              type="button"
              className="eval-btn-ghost"
              style={{ fontSize: 11, padding: '2px 8px' }}
              onClick={prettify}
            >
              Prettify
            </button>
          </div>
        </div>

        <div className="eval-form-group">
          <label className="eval-form-label">
            Type <strong style={{ color: 'var(--text-primary)' }}>{CONFIRM_WORD}</strong> to confirm
          </label>
          <input
            className="eval-form-input"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder={CONFIRM_WORD}
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </div>

        {error && (
          <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="eval-btn-ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button className="eval-btn" onClick={submit} disabled={!confirmed || submitting}>
            {submitting ? 'Saving…' : 'Save config'}
          </button>
        </div>
      </div>
    </div>
  );
};
